import React from 'react';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SaleItem } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/currency';
import MobileListCard from '../common/MobileListCard';

interface SaleItemsBreakdownProps {
  items: SaleItem[];
  ariaLabel: string;
}

const SaleItemsBreakdown: React.FC<SaleItemsBreakdownProps> = ({ items, ariaLabel }) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'), {
    matchMedia: query => window.matchMedia?.(query) ?? {
      matches: false,
      media: query,
      onchange: null,
      addListener: () => {},
      removeListener: () => {},
      addEventListener: () => {},
      removeEventListener: () => {},
      dispatchEvent: () => false,
    },
  });

  if (isCompact) {
    return (
      <Stack spacing={1} aria-label={ariaLabel}>
        {items.map(item => (
          <MobileListCard
            key={item.id}
            title={item.itemName}
            subtitle={item.batchNumber ? `${t('sales.batch')}: ${item.batchNumber}` : undefined}
            fields={[
              { label: t('sales.quantity'), value: item.quantity },
              { label: t('sales.unitNetPrice'), value: formatCurrency(item.unitNetPrice ?? item.unitPrice) },
              { label: `${t('sales.vatAmount')} (%${item.vatRate ?? 0})`, value: formatCurrency(item.vatAmount ?? 0) },
              { label: t('sales.grossAmount'), value: <strong>{formatCurrency(item.totalPrice)}</strong> },
            ]}
          />
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            <TableCell>{t('sales.item')}</TableCell>
            <TableCell align="right">{t('sales.quantity')}</TableCell>
            <TableCell align="right">{t('sales.unitNetPrice')}</TableCell>
            <TableCell align="right">{t('sales.vatRate')}</TableCell>
            <TableCell align="right">{t('sales.vatAmount')}</TableCell>
            <TableCell align="right">{t('sales.grossAmount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id}>
              <TableCell>
                {item.itemName}
                {item.batchNumber && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {t('sales.batch')}: {item.batchNumber}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">{item.quantity}</TableCell>
              <TableCell align="right">{formatCurrency(item.unitNetPrice ?? item.unitPrice)}</TableCell>
              <TableCell align="right">%{item.vatRate ?? 0}</TableCell>
              <TableCell align="right">{formatCurrency(item.vatAmount ?? 0)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'medium' }}>{formatCurrency(item.totalPrice)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SaleItemsBreakdown;
